/**
 * theme.js — Tokens DA centralisés (source unique de vérité).
 *
 * RÈGLE : aucun composant ne redéfinit ces valeurs en local. On importe d'ici.
 * Utilisé par les primitives `daComponents.jsx`, `ModalDA.jsx` et toutes les pages/modals.
 *
 * §20 — Anti-drift token : zéro const couleur/font/radius hors de ce fichier.
 * §18 ter — tout écran DA importe depuis ici, jamais en local.
 */
import { css } from 'styled-components';

export const COLORS = {
  /* Violet (accent principal) */
  accent: '#C218A6',
  accentHover: '#A6148E',
  accent2: '#7a1e8a',
  accent2Deep: '#20005e', /* DA V2 — bouton secondaire profond (View Specs, landing fusion) */
  accent2Soft: 'rgba(122, 30, 138, 0.1)', /* fond léger violet secondaire (chip "Author royalty" Gain) */
  accent2Line: 'rgba(122, 30, 138, 0.4)', /* bordure violet secondaire (chip "Author royalty" Gain) */
  accentSoft: 'rgba(194, 24, 166, 0.08)',
  accent14: 'rgba(194, 24, 166, 0.14)',
  accent45: 'rgba(194, 24, 166, 0.55)',   /* ⚠ nommé 45 mais valeur .55 (legacy) */

  /* Vert CTA */
  cta: '#1FCB5C',
  ctaHover: '#18B050',
  ctaSoft: 'rgba(31, 203, 92, 0.12)', /* fond léger vert (chip Type "buy" Transactions) */
  ctaInk: '#15883d',                  /* texte vert lisible sur fond ctaSoft */
  ctaLine: 'rgba(31, 203, 92, 0.4)',  /* bordure verte (chip "Sale" Gain) */
  goldInk: '#9a6a10',                 /* texte doré lisible sur fond goldSoft (chip "mint") */
  goldLine: 'rgba(232, 172, 58, 0.5)',/* bordure dorée (chip "Commission" Gain) */

  /* Rouge danger */
  danger: '#E0312E',
  dangerDeep: '#B71F1B',
  dangerSoft: 'rgba(224, 49, 46, 0.08)', /* fond léger pour AlertBox/hover danger */
  dangerFill: 'rgba(224, 49, 46, 0.2)',  /* fond pastille danger (BuyLandPanel SorryIcon) */
  dangerLine: 'rgba(224, 49, 46, 0.3)',  /* bordure danger (notice, banner) */

  /* Or (badges tier) */
  gold: '#E8AC3A',
  goldSoft: 'rgba(232, 172, 58, 0.1)', /* fond léger pour AlertBox/warning */
  warnInk: '#7a5600', /* texte lisible sur fond warning (EarniWalletModal AlertText) */

  /* DA V2 — surfaces sombres & accents éditoriaux (previews dav2-*) */
  inkDeep: '#0a0510',        /* footer / fond sombre DA V2 (FooterDark, card thumb) */
  accentBright: '#ff5fd6',   /* rose vif sur fond sombre (eyebrow hero, liens footer) */
  goldBadge: '#f4c84a',      /* dégradé tier badge — clair */
  goldBadgeDeep: '#d98e1f',  /* dégradé tier badge — sombre */
  goldBadgeInk: '#3a2400',   /* texte sur tier badge doré */

  /* Vert CTA secondaire — checkbox / toggle actif (ex: createMultiple, editItem) */
  checkOn: '#4caf50',
  checkOff: '#bdbdbd',

  /* Badge tier verrouillé (gris) — alternative non-or (BuyLandPanel TierBadge $locked) */
  lockGrey: 'rgba(160, 160, 170, 0.85)',

  /* Couleurs de marque tokens (chips réseau — CryptoExchange) */
  avax: '#e84142', /* Avalanche */
  usdc: '#2775ca', /* USDC */

  /* Overlay / ombre */
  overlay: 'rgba(0, 0, 0, 0.55)',    /* fond semi-transparent des modals */
  overlayLight: 'rgba(0, 0, 0, 0.45)', /* overlay de confirmation légère */
  overlayStrong: 'rgba(0, 0, 0, 0.75)', /* fallback sombre opaque (ErrorBoundary page-blanche) */
  /* DA V2 — glass overlays sur média (carte Explore : badge groupe, bouton wishlist) */
  inkGlass: 'rgba(13, 10, 16, 0.55)',   /* pastille sombre translucide sur thumb (group badge) */
  whiteGlass: 'rgba(255, 255, 255, 0.85)', /* bouton clair translucide sur thumb (wishlist) */

  /* DA V2 — blancs translucides sur surface sombre / bandeau accent */
  onDarkBorder: 'rgba(255, 255, 255, 0.08)', /* bordure fine sur HUD sombre (BuyLandPanel) */
  onDarkText: 'rgba(255, 255, 255, 0.25)',   /* texte estompé sur HUD sombre (BuyLandPanel) */
  onAccentFill: 'rgba(255, 255, 255, 0.22)', /* fond actif translucide sur bandeau accent (Settings badge, Bids tab) */
  onAccentBorder: 'rgba(255, 255, 255, 0.35)', /* bordure active translucide sur bandeau accent (Settings badge) */
  onAccentText: 'rgba(255, 255, 255, 0.75)',   /* texte d'onglet actif sur bandeau accent (Land) */
  onAccentTextSoft: 'rgba(255, 255, 255, 0.8)', /* sous-titre clair sur header accent (EarniWalletModal) */

  /* Texte & neutres — DARK MODE : surcouche CSS, valeurs déclarées dans ThemeVars.js
     (`:root` light / `[data-theme="dark"]` dark). Le 2e arg de var() = repli LIGHT exact
     → light mode strictement inchangé même sans la feuille montée. §20 anti-drift préservé. */
  ink: 'var(--ev-ink, #0d0a10)',
  inkSoft: 'var(--ev-ink-soft, rgba(13, 10, 16, 0.78))', /* texte de bio / paragraphe doux (DA V2 profil) */
  muted: 'var(--ev-muted, rgba(13, 10, 16, 0.55))',
  /* ⚠ TODO design : ModalDA.jsx utilise 0.6 (historique) — à aligner après preview HTML */
  inkLine: 'var(--ev-ink-line, rgba(13, 10, 16, 0.2))',  /* bordure ink moyenne (BuyLandPanel LockTag) */
  subtle: 'var(--ev-subtle, rgba(13, 10, 16, 0.08))',
  inkFaint: 'var(--ev-ink-faint, rgba(13, 10, 16, 0.03))', /* fond d'option non sélectionnée (CryptoExchange) */
  line: 'var(--ev-line, #ececec)',
  lineStrong: 'var(--ev-line-strong, #d0d0d0)', /* bordure neutre plus marquée (CryptoExchange AmountField) */
  bg: 'var(--ev-bg, #f4f1f6)',
  bgSubtle: 'var(--ev-bg-subtle, #fbfafc)', /* fond très clair (entête de table DA V2 — thead) */
  white: 'var(--ev-surface, #ffffff)',
  /* Blanc statique (ne s'inverse pas en dark mode) — pour le texte sur fond sombre opaque */
  staticWhite: '#ffffff',

  /* État désactivé des boutons (aligné sur le legacy daComponents : PrimaryButton/CtaButton) */
  disabledBg: 'var(--ev-disabled-bg, #cccccc)',
  disabledInk: 'var(--ev-disabled-ink, #888888)',
};

export const FONTS = {
  /* head = Roboto — standardisé Roboto 2026-06-18 (DM Sans retiré, non chargé). */
  head: "'Roboto', Helvetica, Arial, sans-serif",
  /* body = Roboto — font principale chargée via src/index.css (Google Fonts, poids 400/500/600/700/800/900). */
  body: "'Roboto', Helvetica, Arial, sans-serif",
  /* mono = Roboto Mono — utilisé dans les HUD de coordonnées (BuyLandPanel). */
  mono: "'Roboto Mono', ui-monospace, monospace",
};

export const RADII = {
  card: '1rem',
  button: '0.625rem',
  pill: '1.2rem',
  circle: '50%',
};

export const FONT_SIZE = {
  xs:   '0.75rem',   /* 12px — labels, captions, eyebrows (minimum DA) */
  sm:   '0.875rem',  /* 14px — texte secondaire */
  base: '1rem',      /* 16px — corps de texte */
  lg:   '1.25rem',   /* 20px — sous-titres */
  xl:   '1.5rem',    /* 24px — titres de section */
  xxl:  '2rem',      /* 32px — titres de page */
};

export const FONT_WEIGHT = {
  normal:    400, /* texte régulier */
  medium:    500, /* emphase légère */
  semibold:  600, /* semi-gras */
  bold:      700, /* gras */
  extrabold: 800, /* extra-gras */
  heavy:     900, /* lourd / display */
};

/**
 * Espace vertical réservé sous le header sticky DA V2 (§40 a retiré le hero qui espaçait).
 * Token partagé : appliqué en padding-top du Wrap de chaque page DA V2 → tunable ici en 1 endroit.
 */
/* Header #myHeader est `position: fixed` et mesure ~84px (logo + padding) sur desktop.
   La clearance doit donc dépasser 84px + respiration (l'ancien max 4.5rem=72px coupait
   le 1er élément de ~10px sur toutes les pages PageShell). Mesuré via Chrome MCP 2026-06-10.
   Sous 62rem le header DA V2 passe en flux normal (burger, position relative) → la pleine
   clearance devient ~88px de vide mort en mobile. Le token est donc une variable CSS
   responsive (--header-clear, définie dans App.css au breakpoint 62rem) ; le fallback ici
   préserve le comportement actuel si la var manque (SSR / CSS désactivé / preview UIX). */
export const HEADER_CLEAR = 'var(--header-clear, clamp(5.5rem, 6vw, 6.25rem))';

/**
 * LAYOUT — largeurs de conteneur de PAGE canoniques (§85 — source unique).
 * §20-bis anti-drift largeur : aucun `max-width` de PAGE littéral hors d'ici ;
 * toute page passe par <PageShell> (qui lit ces tokens) — jamais de conteneur de largeur local.
 *   page   82rem  = 1312px  → largeur standard (front + interne) [== BREAKPOINTS.xxl]
 *   narrow 48rem            → formulaires / lecture centrée
 *   wide  102rem            → admin lourde (grandes tables)
 * (Les max-width INTERNES de section/carte restent libres — on ne borne que le conteneur de page.)
 */
export const LAYOUT = {
  page: '82rem',
  narrow: '48rem',
  wide: '102rem',
};

/**
 * GRADIENTS — dégradés DA V2 (source unique, previews dav2-*).
 * §20 anti-drift : aucun `linear-gradient(...)` littéral hors d'ici.
 */
export const GRADIENTS = {
  /* dégradé accent signature (corner ribbon, liserés, accents) */
  accent: 'linear-gradient(135deg, #ea0db0, #4d058e)',
  /* DA V2 landing fusion — coin vert carte Trade */
  cta: 'linear-gradient(135deg, #1FCB5C, #15883d)',
  /* DA V2 landing fusion — badge tier doré (valeurs plates) */
  goldBadgeFlat: 'linear-gradient(135deg, #f4c84a, #d98e1f)',
  /* DA V2 landing fusion — fond radial vignette pour visuels en contain (VR headset) */
  thumbRadial: 'radial-gradient(circle at center, #2a0a3a, #0a0510)',
  /* DA V2 landing fusion — halo magenta derrière la voiture collectible (bande Earn) */
  earnHalo: 'radial-gradient(circle, rgba(255, 95, 214, 0.38), transparent 70%)',
  /* barre/divider accent → transparent (SectionBar, DividerGradient) */
  accentFade: `linear-gradient(90deg, ${COLORS.accent}, transparent)`,
  /* barre/divider danger → transparent (zone dangereuse Admin) */
  dangerFade: `linear-gradient(90deg, ${COLORS.danger}, transparent)`,
  /* fond sombre dégradé (hero, stage modale) */
  dark: 'linear-gradient(180deg, #44005b 0%, #0a0510 100%)',
  /* badge tier doré */
  gold: `linear-gradient(135deg, ${COLORS.goldBadge}, ${COLORS.goldBadgeDeep})`,
  /* voile sombre sur cover utilisateur (lisibilité avatar/nom — DA V2 profil) */
  coverScrim: 'linear-gradient(180deg, rgba(10, 5, 16, 0.05) 35%, rgba(10, 5, 16, 0.82) 100%)',
  /* voile sombre plus léger sur cover (Authors — lisibilité bas de cover) */
  coverScrimFade: 'linear-gradient(180deg, transparent 45%, rgba(10, 5, 16, 0.55))',
  /* hachures sombres — overlay "verrouillé" sur parcelle (BuyLandPanel LockOverlay) */
  lockedStripe:
    'repeating-linear-gradient(45deg, rgba(13, 10, 16, 0.32) 0 0.75rem, rgba(13, 10, 16, 0.12) 0.75rem 1.5rem)',
  /* fond carte "retrait" — blanc → vert léger (Gain $withdraw) */
  withdraw: `linear-gradient(180deg, ${COLORS.white}, ${COLORS.ctaSoft})`,
  /* skeleton shimmer — placeholder de chargement image (SmartImage §39 Phase C) */
  skeleton: `linear-gradient(90deg, ${COLORS.line} 25%, ${COLORS.bgSubtle} 37%, ${COLORS.line} 63%)`,
  /* landing earnicoins — voile clair latéral sur hero (lisibilité texte à gauche) */
  heroScrim:
    'linear-gradient(90deg, rgba(255, 251, 253, 0.88) 0%, rgba(255, 251, 253, 0.52) 30%, rgba(255, 251, 253, 0) 58%)',
  /* landing earnicoins — voile clair vertical sur hero en mobile (texte pleine largeur) */
  heroScrimMobile:
    'linear-gradient(180deg, rgba(255, 251, 253, 0.9), rgba(255, 251, 253, 0.55))',
  /* landing earnicoins — voile SOMBRE sur hero en dark mode (le texte passe en clair via
     COLORS.ink → doit reposer sur un fond sombre, sinon délavé sur l'image claire). */
  heroScrimDark:
    'linear-gradient(90deg, rgba(10, 5, 16, 0.92) 0%, rgba(10, 5, 16, 0.55) 30%, rgba(10, 5, 16, 0) 58%)',
  heroScrimMobileDark:
    'linear-gradient(180deg, rgba(10, 5, 16, 0.92), rgba(10, 5, 16, 0.55))',
  /* landing earnicoins — voile sombre sur bannière CTA finale (texte blanc lisible) */
  finalScrim: 'linear-gradient(180deg, rgba(10, 5, 16, 0.5), rgba(10, 5, 16, 0.8))',
  /* landing earnicoins — fond léger de la ligne earnicoins/mois (carte earnipass) */
  ecRow: 'linear-gradient(90deg, rgba(194, 24, 166, 0.07), rgba(194, 24, 166, 0.02))',
  /* landing earnicoins — fond VIOLET de la carte earnipass en dark mode (carte premium). */
  passDark: 'linear-gradient(155deg, #4a1173 0%, #2a0a45 58%, #180530 100%)',
};

export const SHADOW = {
  card: '0 0.3rem 1rem rgba(13, 10, 16, 0.04)',
  /* DA V2 landing fusion — cadre média hero + gallery */
  heroMedia: '0 1.5rem 3.75rem rgba(60, 5, 80, 0.22)',
  /* DA V2 landing fusion — carte download Epic */
  dlcard: '0 1.125rem 3.125rem rgba(60, 10, 70, 0.08)',
  /* DA V2 landing fusion — filtre ombre + glow voiture collectible (valeur `filter:`) */
  earnCar: 'drop-shadow(0 1.375rem 2.375rem rgba(0, 0, 0, 0.55)) drop-shadow(0 0 1.75rem rgba(255, 95, 214, 0.2))',
  hover: '0 0.6rem 1.6rem rgba(13, 10, 16, 0.09)',
  /* ombre portée de l'avatar profil (teinte violacée — DA V2 profil) */
  avatar: '0 0.875rem 2.125rem rgba(60, 5, 80, 0.35)',
  /* ombre douce sur avatar bordé (Authors Avatar) */
  coverInset: '0 0.375rem 1rem rgba(13, 10, 16, 0.3)',
  /* ombre de menu/dropdown clair (BuyLandPanel LockTag) */
  dropdown: '0 0.1875rem 0.5625rem rgba(0, 0, 0, 0.15)',
  /* ombre portée de carte modale (EarniWalletModal) */
  modal: '0 1rem 3rem rgba(0, 0, 0, 0.25)',
  /* halos colorés des CTA biseautés (BuyLandPanel) */
  goldGlow: '0 0.375rem 1.125rem rgba(180, 120, 10, 0.35)', /* badge tier doré */
  ctaGlow: '0 0.625rem 1.625rem rgba(31, 203, 92, 0.32)',
  ctaGlowHover: '0 0.875rem 2rem rgba(31, 203, 92, 0.4)',
  dangerGlow: '0 0.625rem 1.625rem rgba(224, 49, 46, 0.28)',
  accentGlow: '0 0.625rem 1.625rem rgba(194, 24, 166, 0.32)',
  accentGlowHover: '0 0.875rem 2rem rgba(194, 24, 166, 0.4)',
};

/**
 * STATUS — palettes de pastilles de statut (fond / texte / bordure).
 * §20 anti-drift : aucun #hex de statut hors d'ici (Settings StatusPill, notes).
 */
export const STATUS = {
  red: { bg: '#fff3f3', ink: '#b42318', line: '#f5c2c2' },   /* programmé / échec */
  blue: { bg: '#eef6ff', ink: '#1849a9', line: '#b2d4f5' },  /* exécuté / réglé */
  green: { bg: '#eafaef', ink: '#1a7f37', line: '#bfe6cb' }, /* annulé / envoyé / ok */
};

/**
 * BREAKPOINTS — système de breakpoints centralisé (§12 ter).
 * Tous alignés sur les valeurs existantes dans la codebase pour éviter toute régression.
 * Utiliser via le helper `media` ci-dessous — jamais de px en dur dans @media.
 *
 * Correspondances px :
 *   sm  30rem  = 480px
 *   mob 40rem  = 640px   (palier mobile §95 : empilement grilles/filtres)
 *   md  48rem  = 768px
 *   lg  62rem  = 992px   (BuyLandPanel existant)
 *   xl  73.75rem = 1180px (BuyLandPanel existant)
 *   xxl 82rem  = 1312px  (Container max-width)
 */
export const BREAKPOINTS = {
  sm: '30rem',
  mob: '40rem',
  md: '48rem',
  lg: '62rem',
  xl: '73.75rem',
  xxl: '82rem',
};

/**
 * media(bp) — helper responsive (§12 ter).
 * Usage : `${media.md`color: red;`}` dans un styled-component.
 * Safe en styled-components v5.3.3 (interpolation css tag confirmée).
 *
 * @param {string} bp — clé de BREAKPOINTS
 * @returns tagged template literal
 *
 * @example
 *   const Title = styled.h1`
 *     font-size: ${FONT_SIZE.xxl};
 *     ${media.md`font-size: ${FONT_SIZE.xl};`}
 *   `;
 */
export const media = Object.keys(BREAKPOINTS).reduce((acc, key) => {
  acc[key] = (...args) => css`
    @media (max-width: ${BREAKPOINTS[key]}) {
      ${css(...args)}
    }
  `;
  return acc;
}, {});

/**
 * SPACE — échelle d'espacement relative (§12 ter — zéro px en dur).
 * Usage : padding: ${SPACE.lg} ${SPACE.xl};
 */
export const SPACE = {
  xs: '0.25rem',
  sm: '0.5rem',
  md: '0.75rem',
  lg: '1rem',
  xl: '1.5rem',
  xxl: '2rem',
  xxxl: '3rem',
};

/**
 * ECOLORS — palette « legacy brand » absorbée de src/utils/constants/colors.enum.ts
 * (P2-6 lot C, 2026-07-04). Valeurs historiques strictement inchangées.
 * DÉPRÉCIÉ pour tout nouveau code : utiliser COLORS (palette DA canonique).
 * PRIMARY_DARK partage sa valeur avec COLORS.accent2Deep (source unique).
 */
export const ECOLORS = Object.freeze({
  PRIMARY: '#ffffff',
  PRIMARY_DARK: COLORS.accent2Deep, /* #20005e */
  PRIMARY_LIGHT: '#f312a2',
  SECONDARY: '#ff2d55',
  SECONDARY_DARK: '#a706aa',
  BLACK: '#16141a',
  GREY: '#f2f2f2',
  GREY_DARK: '#a59f9f',
  RED: '#f44336',
  RED_SECONDARY: '#e2747e',
  GREEN_SECONDARY: '#abd804',
  PURPLE: '#a706aa',
});
